import useAuthentication from 'hooks/useAuthentication';
import PageLayout from 'layouts/PageLayout';
import { GetServerSideProps } from 'next';
import { Session, getServerSession } from 'next-auth';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { authOptions } from 'pages/api/auth/[...nextauth]';
import { useCallback, useMemo } from 'react';
import {
  useGetMyProfilePrivatePostsInfiniteQuery,
  useGetMyProfilePublicPostsInfiniteQuery,
  useGetProfilePrivatePostsInfiniteQuery,
  useGetProfilePublicPostsInfiniteQuery,
} from 'store/services/contentApi';
import { useGetMutualFriendsQuery } from 'store/services/graphsApi';
import { useGetRelevantCreatorsQuery } from 'store/services/graphsApi';
import { PublicUserProfile, PublicUserProfileWithSocialStatus } from 'types';
import { getAppsFlyerDeeplink } from 'utils/deeplinks';
import { getMyProfile, getPublicProfile, getPublicProfileAsLoggedInUser } from 'utils/fetch';
import { getCurrentUrl, isListEmpty } from 'utils/helper';
import { POSTGRID_POSTS_SIZE } from '@/features/posts/constants';
import UploadYourFirstPostHint from 'assets/hints/upload-your-first-post.svg';
import PostsTabs from '@/components/Profile/PostsTabs';

const Profile = dynamic(() => import('components/Profile'), { ssr: false });
const PostsGrid = dynamic(() => import('components/PostsGrid'), { ssr: false });
const EmptyProfile = dynamic(() => import('components/Profile/Empty'), { ssr: false });
const UseMobileAppPrompt = dynamic(() => import('@/components/UseMobileAppPrompt'), { ssr: false });
const OnlyAvailableInAppPrompt = dynamic(() => import('@/components/OnlyAvailableInAppPrompt'), {
  ssr: false,
});

const User: React.FC<PublicUserProfile | PublicUserProfileWithSocialStatus> = (profile) => {
  const router = useRouter();
  const { citizenUsername } = router.query;

  const { isUserAuthenticated, user: loggedInUser, isLoading: isAuthLoading } = useAuthentication();

  const isMe = loggedInUser?.id === profile.id;

  const isMyFriend = 'isFriends' in profile && profile.isFriends;
  const amIFollowing = 'amIFollowing' in profile && profile.amIFollowing;
  const isLoveMeterVisible = isMe || profile?.loveMeterVisibility === 'Anyone';
  const isCreator =
    profile?.moderationStatus === 'approved' || profile?.moderationStatus === 'auto_approved';
  loggedInUser?.id === profile.id;
  const { tab } = router.query;
  const isValidPostTab = tab === 'public' || tab === 'private';
  const activePostTab = isValidPostTab ? (isMyFriend || isMe ? tab : 'public') : 'public';
  const skip = isAuthLoading;

  const {
    data: publicPostData,
    fetchNextPage: fetchNextPublicPosts,
    hasNextPage: hasMorePublicPosts,
    isFetching: isPublicPostsFetching,
    isSuccess: isPublicPostsSuccess,
  } = useGetProfilePublicPostsInfiniteQuery(
    {
      size: POSTGRID_POSTS_SIZE,
      username: profile.username,
    },
    {
      skip: skip || isMe,
    }
  );

  const {
    data: myPublicPostData,
    fetchNextPage: fetchNextMyPublicPosts,
    hasNextPage: hasMoreMyPublicPosts,
    isFetching: isMyPublicPostsFetching,
    isSuccess: isMyPublicPostsSuccess,
  } = useGetMyProfilePublicPostsInfiniteQuery(
    {
      size: POSTGRID_POSTS_SIZE,
    },
    {
      skip: skip || !isMe,
    }
  );

  const publicPosts = (isMe ? myPublicPostData : publicPostData)?.pages.flatMap(
    (page) => page.data
  );
  const isPublicFetching = isMyPublicPostsFetching || isPublicPostsFetching;
  const isPublicSuccess = isMyPublicPostsSuccess || isPublicPostsSuccess;
  const fetchNextPublic = isMe
    ? hasMoreMyPublicPosts
      ? fetchNextMyPublicPosts
      : undefined
    : hasMorePublicPosts
    ? fetchNextPublicPosts
    : undefined;

  const {
    data: privatePostData,
    fetchNextPage: fetchNextPrivatePosts,
    hasNextPage: hasMorePrivatePosts,
    isFetching: isPrivatePostsFetching,
    isSuccess: isPrivatePostsSuccess,
  } = useGetProfilePrivatePostsInfiniteQuery(
    {
      size: POSTGRID_POSTS_SIZE,
      citizenId: profile.id,
    },
    {
      skip: skip || isMe || !isMyFriend,
    }
  );

  const {
    data: myPrivatePostData,
    fetchNextPage: fetchNextMyPrivatePosts,
    hasNextPage: hasMoreMyPrivatePosts,
    isFetching: isMyPrivatePostsFetching,
    isSuccess: isMyPrivatePostsSuccess,
  } = useGetMyProfilePrivatePostsInfiniteQuery(
    {
      size: POSTGRID_POSTS_SIZE,
    },
    {
      skip: skip || !isMe,
    }
  );

  const privatePosts = (isMe ? myPrivatePostData : privatePostData)?.pages.flatMap(
    (page) => page.data
  );
  const isPrivateFetching = isMyPrivatePostsFetching || isPrivatePostsFetching;
  const isPrivateSuccess = isMyPrivatePostsSuccess || isPrivatePostsSuccess;
  const fetchNextPrivate = isMe
    ? hasMoreMyPrivatePosts
      ? fetchNextMyPrivatePosts
      : undefined
    : hasMorePrivatePosts
    ? fetchNextPrivatePosts
    : undefined;

  const isPostsFetching = isPublicFetching || isPrivateFetching;
  const isPostsSuccess = isPublicSuccess && isPrivateSuccess;

  const tabPosts = useMemo(
    () => (activePostTab === 'private' ? privatePosts ?? [] : publicPosts ?? []),
    [privatePosts, publicPosts, activePostTab]
  );

  const { data: mutualConnections } = useGetMutualFriendsQuery(
    { limit: 20, offset: 0, citizenId: profile.id },
    { skip: !isUserAuthenticated || isMe }
  );

  const { data: suggestedCreators, isLoading: isSuggestedCreatorsLoading } =
    useGetRelevantCreatorsQuery(
      { offset: 0, limit: 8, citizenId: isMe ? undefined : profile.id },
      { skip: !isUserAuthenticated }
    );

  const iHaveNoPosts =
    isMe &&
    !isPostsFetching &&
    isPostsSuccess &&
    isListEmpty(publicPosts) &&
    isListEmpty(privatePosts);

  const handleTabClick = useCallback(
    (tab: 'public' | 'private') => {
      router.replace(`/${citizenUsername}?tab=${tab}`, undefined, {
        shallow: true,
      });
    },
    [citizenUsername, router]
  );

  const fetchMore = useMemo(
    () => (activePostTab === 'private' ? fetchNextPrivate : fetchNextPublic),
    [activePostTab, fetchNextPrivate, fetchNextPublic]
  );
  return (
    <>
      <Head>
        <title>{`${Boolean(profile?.username) ? profile.username : 'Profile'} | WeAre8`}</title>
        <meta property="og:type" content="website" />
        <meta property="og:url" content={getCurrentUrl()} />
        <meta name="description" content={profile.bio ?? ''} />
        <meta property="og:title" content={`${profile.username} on WeAre8`} />
        <meta property="og:description" content={profile.bio ?? ''} />
        <meta property="og:site_name" content="WeAre8" />
        <meta property="og:image" content={profile.profilePictureUri} />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:alt" content="profile preview" />
        <meta property="og:image:width" content="500" />
        <meta property="og:image:height" content="500" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${profile.username} on WeAre8`} />
        <meta name="twitter:image" content={profile.profilePictureUri} />
      </Head>
      <PageLayout withoutPadding key={citizenUsername as string}>
        {!profile?.id ? (
          <EmptyProfile />
        ) : (
          <>
            <Profile
              profile={profile}
              isCreator={isCreator}
              isLoveMeterVisible={isLoveMeterVisible}
              isMe={isMe}
              isMyFriend={isMyFriend}
              amIFollowing={amIFollowing}
              isUserLoggedIn={isUserAuthenticated}
              mutualConnections={mutualConnections}
              suggestions={suggestedCreators?.data ?? []}
              isSuggestionsLoading={isSuggestedCreatorsLoading}
            />
            {iHaveNoPosts ? (
              <UploadYourFirstPostHint style={{ margin: 'auto', width: '100%' }} />
            ) : (
              <div className="posts">
                {(isMyFriend || isMe) && Boolean(publicPosts) && Boolean(privatePosts) && (
                  <PostsTabs onTabClick={handleTabClick} activeTab={activePostTab} />
                )}
                <div className="content">
                  <PostsGrid fetchMore={fetchMore} posts={tabPosts} isLoading={isPostsFetching} />
                </div>
              </div>
            )}
          </>
        )}
        <UseMobileAppPrompt deepLink={`profile?userId=${profile.id}`} />
        <OnlyAvailableInAppPrompt deepLink={`profile?userId=${profile.id}`} />
      </PageLayout>
    </>
  );
};

export default User;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { params } = context;
  context.res.setHeader('Cache-Control', 'public, s-maxage=10, stale-while-revalidate=59');
  const originalParam = params?.citizenUsername as string;

  if (!originalParam || !originalParam?.startsWith('@')) {
    return {
      notFound: true,
    };
  }
  const citizenUsername = originalParam.substring(1);

  const session = (await getServerSession(context.req, context.res, authOptions)) as Session | null;

  if (!session) {
    const profile = await getPublicProfile(citizenUsername);
    return { props: profile ?? {} };
  }

  const accessToken = session.user.accessToken as string;

  if (session.user?.username !== citizenUsername) {
    const otherProfile = await getPublicProfileAsLoggedInUser(citizenUsername, accessToken);
    return { props: otherProfile ?? {} };
  }

  const myProfile = await getMyProfile(accessToken);
  return { props: myProfile ?? {} };
};
